import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { HelperFunctions, IListingRating, RatingType, ReviewType } from '@curbnturf/entities';
import { RatingApiService } from '../backend-api/rating-api.service';
import { AuthService } from '../auth/auth.service';
import { measureDataUrl } from '../photos/measure.util';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

export interface IReviewSubScores {
  cleanliness: number;
  communication: number;
  location: number;
  hookups: number;
  value: number;
}

/** Host's public reply to a guest's review. One per review, edit/remove allowed.
 *  Stays local/mock-only — out of scope for the real-backend review conversion
 *  (no backend concept of a host reply to a review exists today). */
export interface IHostReviewResponse {
  text: string;
  respondedAt: string; // ISO
}

export interface IUserReview {
  id: string;
  bookingId: string;
  listingId: number;
  userEmail: string;
  authorName: string;
  authorInitials: string;
  rating: number; // 1–5 (overall)
  text: string;
  subScores: IReviewSubScores;
  createdAt: string;
  /** Optional host reply, rendered under the review. */
  hostResponse?: IHostReviewResponse;
  /** Guest-attached photo URLs (S3-backed once uploaded). */
  photos?: string[];
}

const HELPFUL_KEY = 'cnt-review-helpful';
const HELPFUL_IDB_KEY = 'review:helpful';
/** Host replies to reviews — see `IHostReviewResponse` doc: local-only, keyed
 *  by `bookingId` (stable across re-hydration, unlike a review's own id which
 *  only exists once a real review has been submitted). */
const HOST_RESPONSES_KEY = 'cnt-review-host-responses';
const HOST_RESPONSES_IDB_KEY = 'review:host-responses';

/** Average of the review sub-scores. Drives the derived overall rating.
 * Off-grid stays (no hookups) skip the Hookups score so an N/A field can't
 * drag the overall down. */
export function averageSubScores(s: IReviewSubScores, excludeHookups = false): number {
  if (excludeHookups) return (s.cleanliness + s.communication + s.location + s.value) / 4;
  return (s.cleanliness + s.communication + s.location + s.hookups + s.value) / 5;
}

/** Star-rendering state for position `pos` (1..5) against a fractional rating.
 *   pos=5, rating=4.8 → 'half'   pos=4, rating=4.8 → 'full'
 *   pos=5, rating=4.2 → 'empty' (rating below 4.5)
 */
export function starState(pos: number, rating: number): 'full' | 'half' | 'empty' {
  if (rating >= pos) return 'full';
  if (rating >= pos - 0.5) return 'half';
  return 'empty';
}

const SUB_SCORE_TOPICS: (keyof IReviewSubScores)[] = ['cleanliness', 'communication', 'location', 'hookups', 'value'];

/** A review photo staged for upload — already rotated by the PhotoEditor
 *  flow; `crop` is normalized 0..1, converted to backend pixel offsets at
 *  submit time (see `submitReal`). */
export interface IReviewPhotoUpload {
  file: File;
  crop: { left: number; top: number; right: number; bottom: number };
}

export interface ISubmitReviewParams {
  listingId: number;
  reservationId: number;
  subScores: IReviewSubScores;
  /** False for off-grid listings — the Hookups sub-score is dropped from
   *  both submission and the computed overall. */
  includeHookups: boolean;
  text: string;
  photos: IReviewPhotoUpload[];
  authorName: string;
  authorInitials: string;
}

/**
 * Real-backed guest reviews — talks to curbnturf's `/rating/listing` API via
 * the plain, NgRx-free `RatingService` (`@curbnturf/rating`, imported by deep
 * path since the lib's barrel only exports the NgRx `RatingFacade`/module —
 * same reasoning `MessageService` documents for its own deep import).
 *
 * There is no mock/local fallback for review *content* — a review only
 * exists once it's been submitted to and returned by the real backend.
 * `helpfulCount`/`hasUserVoted`/`toggleHelpful`/`setHostResponse` remain
 * local/mock-only by design (no backend equivalent exists), unaffected by
 * this file's conversion.
 *
 * `GET rating/listing/:listingId` is listing-scoped public data, not
 * user-scoped, so there's no single "load everything" call to make on
 * construction the way `MessageService`/`FavoritesService` do — callers must
 * `await hydrateListing(listingId)` before the synchronous read methods
 * (`list()`, `forListing()`, `forBooking()`, `forUser()`, `aggregateRating()`)
 * reflect that listing's reviews.
 *
 * Known, deliberate gap: the backend hides a review (`hidden: true`) until
 * the host has also rated the guest back (or a 21-day cron auto-reveals it).
 * A submitter's own just-posted review is kept in this session's in-memory
 * cache immediately (see `submitReal`), but a fresh page load's
 * `hydrateListing()` won't return it again until the reveal gate flips.
 */
@Injectable({ providedIn: 'root' })
export class ReviewService {
  private platformId = inject(PLATFORM_ID);
  private auth = inject(AuthService);
  private rating = inject(RatingApiService);

  private readonly _reviews$ = new BehaviorSubject<IUserReview[]>([]);
  readonly reviews$: Observable<IUserReview[]> = this._reviews$.asObservable();

  /** reviewId → set of user emails who voted helpful. Emits on every
   *  toggle so consumers can re-render counts without resubscribing
   *  to the reviews stream itself. */
  private readonly _helpful$ = new BehaviorSubject<Record<string, string[]>>({});
  readonly helpful$: Observable<Record<string, string[]>> = this._helpful$.asObservable();

  private readonly _hostResponses$ = new BehaviorSubject<Record<string, IHostReviewResponse>>({});

  private readonly helpfulStore = createIdbKvStore<Record<string, string[]>>({
    legacyKeys: { [HELPFUL_IDB_KEY]: HELPFUL_KEY },
  });
  private readonly hostResponsesStore = createIdbKvStore<Record<string, IHostReviewResponse>>({
    legacyKeys: { [HOST_RESPONSES_IDB_KEY]: HOST_RESPONSES_KEY },
  });

  constructor() {
    this._helpful$.next(this.readHelpful());
    this._hostResponses$.next(this.readHostResponses());
    if (isPlatformBrowser(this.platformId)) {
      // Both reads above likely return {} pre-hydration — re-emit once the
      // stores' caches actually reflect any previously-saved data.
      void Promise.all([this.helpfulStore.init(), this.hostResponsesStore.init()]).then(() => {
        this._helpful$.next(this.readHelpful());
        this._hostResponses$.next(this.readHostResponses());
      });
      this.auth.loggedOut$.subscribe(() => {
        this.writeHelpful({});
        this.writeHostResponses({});
      });
    }
  }

  // ---- Hydration (async — real HTTP) --------------------------------------

  /** Fetch and cache this listing's visible reviews. Replaces only this
   *  listing's entries in the cache — other listings' cached/optimistic
   *  entries are left alone. */
  async hydrateListing(listingId: number): Promise<void> {
    if (!listingId) return;
    const rows = await firstValueFrom(this.rating.fetchListingRatings(listingId));
    const reviews = this.groupRows(rows);
    const others = this._reviews$.value.filter((r) => r.listingId !== listingId);
    this._reviews$.next([...others, ...reviews]);
  }

  private groupRows(rows: IListingRating[]): IUserReview[] {
    const groups = new Map<string, IListingRating[]>();
    for (const row of rows) {
      const key = `${row.reservationId}:${row.creatorId}`;
      const arr = groups.get(key);
      if (arr) arr.push(row);
      else groups.set(key, [row]);
    }

    const reviews: IUserReview[] = [];
    for (const group of groups.values()) {
      const overall = group.find((r) => r.topic === RatingType.overall);
      // A rating row can exist without review text yet (e.g. a default
      // rating used to unlock messaging) — that's not a review.
      if (!overall || !overall.score?.description) continue;

      const creator = overall.creator;
      const authorName = `${creator?.firstName ?? ''} ${creator?.lastName ?? ''}`.trim() || 'Guest';
      const authorInitials =
        ((creator?.firstName?.[0] ?? '') + (creator?.lastName?.[0] ?? '')).toUpperCase() ||
        authorName.slice(0, 2).toUpperCase();
      const fallback = overall.score?.score ?? 0;
      const findScore = (topic: RatingType) => group.find((r) => r.topic === topic)?.score?.score;

      reviews.push({
        id: String(overall.id),
        bookingId: `res-${overall.reservationId}`,
        listingId: overall.ratedId ?? 0,
        userEmail: '',
        authorName,
        authorInitials,
        rating: overall.score?.score ?? 0,
        text: overall.score.description,
        subScores: {
          cleanliness: findScore(RatingType.cleanliness) ?? fallback,
          communication: findScore(RatingType.communication) ?? fallback,
          location: findScore(RatingType.location) ?? fallback,
          hookups: findScore(RatingType.hookups) ?? fallback,
          value: findScore(RatingType.value) ?? fallback,
        },
        createdAt: overall.created ? new Date(overall.created * 1000).toISOString() : new Date().toISOString(),
        photos: (overall.photos ?? []).map((p) => HelperFunctions.getPhotoUrlSquareMedium(p)),
      });
    }
    return reviews;
  }

  // ---- Submission (async — real HTTP) -------------------------------------

  /** The only way to submit a review — sub-score ratings, then the review
   *  text, then any photos, in sequence. Optimistically caches the result
   *  so the submitter sees it immediately (see the class doc's reveal-gate
   *  note for why a fresh reload won't show it again right away). */
  async submitReal(params: ISubmitReviewParams): Promise<IUserReview> {
    const user = this.auth.currentUser;
    if (!user?.id) throw new Error('Must be signed in to submit a review');
    const creatorId = user.id;

    const subScoreRatings: IListingRating[] = SUB_SCORE_TOPICS.filter(
      (key) => key !== 'hookups' || params.includeHookups,
    ).map((key) => ({
      topic: RatingType[key],
      score: { score: params.subScores[key], description: '' },
      ratedId: params.listingId,
      reservationId: params.reservationId,
      creatorId,
      creator: { id: creatorId },
    }));
    await firstValueFrom(this.rating.createListingRatings(subScoreRatings));

    const reviewResult = await firstValueFrom(
      this.rating.reviewListing(params.listingId, {
        reviewType: ReviewType.listing,
        review: params.text,
        reservationId: params.reservationId,
        listingId: params.listingId,
        creatorId,
        creator: { id: creatorId },
      }),
    );

    let photoUrls: string[] = [];
    for (const photo of params.photos) {
      const objectUrl = URL.createObjectURL(photo.file);
      let width: number;
      let height: number;
      try {
        ({ width, height } = await measureDataUrl(objectUrl));
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
      const formData = new FormData();
      formData.append('files', photo.file);
      // Rotation is already baked into the file's pixels by the PhotoEditor
      // flow — only the crop rect (converted from normalized 0..1 to the
      // pixel offsets the backend's shared crop renderer expects) is sent.
      formData.append('cropLeft', String(Math.round(photo.crop.left * width)));
      formData.append('cropTop', String(Math.round(photo.crop.top * height)));
      formData.append('cropRight', String(Math.round(photo.crop.right * width)));
      formData.append('cropBottom', String(Math.round(photo.crop.bottom * height)));
      formData.append('rotation', '0');
      const photos = await firstValueFrom(
        this.rating.uploadReviewPhoto(params.listingId, reviewResult.id as number, formData),
      );
      photoUrls = photos.map((p) => HelperFunctions.getPhotoUrlSquareMedium(p));
    }

    const review: IUserReview = {
      id: String(reviewResult.id),
      bookingId: `res-${params.reservationId}`,
      listingId: params.listingId,
      userEmail: user.email,
      authorName: params.authorName,
      authorInitials: params.authorInitials,
      rating: averageSubScores(params.subScores, !params.includeHookups),
      text: params.text,
      subScores: params.subScores,
      createdAt: new Date().toISOString(),
      photos: photoUrls.length ? photoUrls : undefined,
    };
    this.upsertLocal(review);
    return review;
  }

  private upsertLocal(review: IUserReview): void {
    const next = this._reviews$.value.filter((r) => r.bookingId !== review.bookingId);
    next.push(review);
    this._reviews$.next(next);
  }

  // ---- Public read API (sync — reads the in-memory cache) -----------------

  list(): IUserReview[] {
    return this.applyHostResponses(this._reviews$.value);
  }

  forBooking(bookingId: string): IUserReview | null {
    return this.list().find((r) => r.bookingId === bookingId) ?? null;
  }

  forListing(listingId: number): IUserReview[] {
    return this.list()
      .filter((r) => r.listingId === listingId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  forUser(email: string): IUserReview[] {
    return this.list().filter((r) => r.userEmail === email);
  }

  /** Combine the listing's own real aggregate rating with whatever's in the
   *  hydrated cache (only relevant when the cache holds reviews the listing's
   *  own `rating`/`reviewCount` hasn't yet accounted for — e.g. a
   *  just-submitted optimistic entry). */
  aggregateRating(seededRating: number, seededCount: number, listingId: number): { rating: number; count: number } {
    const cached = this.list().filter((r) => r.listingId === listingId);
    if (cached.length === 0) return { rating: seededRating, count: seededCount };
    const sum = cached.reduce((total, r) => total + r.rating, 0);
    const totalCount = seededCount + cached.length;
    const totalSum = seededRating * seededCount + sum;
    return { rating: +(totalSum / totalCount).toFixed(2), count: totalCount };
  }

  // ---- Helpful votes (local-only, out of scope — unchanged) ---------------

  /** Number of unique helpful votes for a review. */
  helpfulCount(reviewId: string): number {
    return (this._helpful$.value[reviewId] ?? []).length;
  }

  /** True when the given user has already voted helpful on a review. */
  hasUserVoted(reviewId: string, userEmail: string): boolean {
    return (this._helpful$.value[reviewId] ?? []).includes(userEmail);
  }

  /** Toggle a user's helpful vote on a review. Returns the new vote
   *  state ('voted' | 'unvoted'). Per-user persisted via localStorage. */
  toggleHelpful(reviewId: string, userEmail: string): 'voted' | 'unvoted' {
    if (!reviewId || !userEmail) return 'unvoted';
    const all = { ...this._helpful$.value };
    const set = new Set(all[reviewId] ?? []);
    let result: 'voted' | 'unvoted';
    if (set.has(userEmail)) {
      set.delete(userEmail);
      result = 'unvoted';
    } else {
      set.add(userEmail);
      result = 'voted';
    }
    all[reviewId] = [...set];
    if (all[reviewId].length === 0) delete all[reviewId];
    this.writeHelpful(all);
    return result;
  }

  // ---- Host response (local-only, out of scope — unchanged behavior) ------

  /** Set / replace / clear the host's public response on a review. Pass null
   * (or empty/whitespace) to remove. Returns the updated review or null if
   * the booking isn't reviewed. */
  setHostResponse(bookingId: string, text: string | null): IUserReview | null {
    if (!this.forBooking(bookingId)) return null;
    const trimmed = text?.trim() ?? '';
    const all = { ...this._hostResponses$.value };
    if (trimmed) {
      all[bookingId] = { text: trimmed, respondedAt: new Date().toISOString() };
    } else {
      delete all[bookingId];
    }
    this.writeHostResponses(all);
    return this.forBooking(bookingId);
  }

  private applyHostResponses(reviews: IUserReview[]): IUserReview[] {
    const map = this._hostResponses$.value;
    if (Object.keys(map).length === 0) return reviews;
    return reviews.map((r) => (map[r.bookingId] ? { ...r, hostResponse: map[r.bookingId] } : r));
  }

  private readHelpful(): Record<string, string[]> {
    if (!isPlatformBrowser(this.platformId)) return {};
    return this.helpfulStore.read(HELPFUL_IDB_KEY) ?? {};
  }

  private writeHelpful(map: Record<string, string[]>): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.helpfulStore.write(HELPFUL_IDB_KEY, map);
    }
    this._helpful$.next(map);
  }

  private readHostResponses(): Record<string, IHostReviewResponse> {
    if (!isPlatformBrowser(this.platformId)) return {};
    return this.hostResponsesStore.read(HOST_RESPONSES_IDB_KEY) ?? {};
  }

  private writeHostResponses(map: Record<string, IHostReviewResponse>): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.hostResponsesStore.write(HOST_RESPONSES_IDB_KEY, map);
    }
    this._hostResponses$.next(map);
  }
}
